import CustomPaper from '../../../containers/Paper'
import { useContext, useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { StatisticsContext } from '../../../context/StatisticsContext'
import StatisticsService from '../../../services/StatisticsService'
import CustomIconButton from '../../../components/IconButton'
import DownloadIcon from '@mui/icons-material/Download'
import { PDFDownloadLink } from '@react-pdf/renderer'
import CustomTable from '../../../containers/Table'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import PostgraduateStudiesPDF from './PostgraduateStudiesPDF'
import { months } from '../../../helpers/enums'
import dayjs from 'dayjs'
import { PageContext } from '../../../context/PageContext'

interface EducationalHistoryType {
    degree: string
    school: string
    startDate: string
    endDate: string
    country: string
    areaOfStudy: string
}

interface WorkingAbroadUserType {
    firstName: string
    lastName: string
    country: string
    phone: string
    email: string
    currentEmployer: string
}

export interface PostgraduateDataType {
    workingAbroadUser: WorkingAbroadUserType
    educationalHistory: EducationalHistoryType
}

const PostgraduateStudies = () => {
    const { startDate, endDate, carreer } = useContext(StatisticsContext)
    const [data, setData] = useState<PostgraduateDataType[]>()
    const { smallScreen } = useContext(PageContext)

    useEffect(() => {
        new StatisticsService()
            .getGraduatesWithPostGraduateDegrees(carreer, startDate, endDate)
            .then((response) => {
                if (response.status !== 200) return
                let result: PostgraduateDataType[] = response.data
                result.sort(
                    (x, y) =>
                        dayjs(y.educationalHistory.endDate).unix() -
                        dayjs(x.educationalHistory.endDate).unix(),
                )
                result = result.filter((x) => dayjs(x.educationalHistory.endDate).isBefore(dayjs()))
                setData(result)
            })
    }, [carreer, startDate, endDate])

    const columnHelper = createColumnHelper<PostgraduateDataType>()
    const columns: ColumnDef<PostgraduateDataType, any>[] = [
        columnHelper.accessor('workingAbroadUser', {
            header: 'Nombre',
            cell: ({ row }) => (
                <div className="text-sm text-secondary">
                    {row.original.workingAbroadUser.firstName}{' '}
                    {row.original.workingAbroadUser.lastName}
                </div>
            ),
        }),
        columnHelper.accessor('workingAbroadUser', {
            header: 'Campo de estudio',
            cell: ({ row }) => (
                <div className="text-sm text-secondary">
                    {row.original.educationalHistory.areaOfStudy}
                </div>
            ),
        }),
    ]

    if (!smallScreen) {
        columns.push(
            columnHelper.accessor('workingAbroadUser', {
                header: 'Email',
                cell: ({ row }) => (
                    <div className="text-sm text-secondary">
                        {row.original.workingAbroadUser.email}
                    </div>
                ),
            }),
            columnHelper.accessor('workingAbroadUser', {
                header: 'Institución',
                cell: ({ row }) => (
                    <div className="text-sm text-secondary">
                        {row.original.educationalHistory.school}
                    </div>
                ),
            }),
            columnHelper.accessor('workingAbroadUser', {
                header: 'País',
                cell: ({ row }) => (
                    <div className="text-sm text-secondary">
                        {row.original.educationalHistory.country.split(/(?=[A-Z])/).join(' ')}
                    </div>
                ),
            }),
            columnHelper.accessor('workingAbroadUser', {
                header: 'Fecha',
                cell: ({ row }) => (
                    <div className="text-sm text-secondary">
                        {
                            months[
                                (new Date(row.original.educationalHistory.endDate).getMonth() +
                                    1) as keyof typeof months
                            ]
                        }{' '}
                        {new Date(row.original.educationalHistory.endDate).getFullYear()}
                    </div>
                ),
            }),
        )
    }

    return (
        <CustomPaper className="h-full flex flex-col gap-1 text-primary">
            <div className="flex gap-2 font-semibold text-2xl">
                <span className="text-lg font-semibold">Graduados con posgrados</span>
                {data && (
                    <PDFDownloadLink
                        document={<PostgraduateStudiesPDF data={data} />}
                        fileName="Reporte_Graduados_Con_Posgrados.pdf"
                    >
                        <CustomIconButton
                            icon={<DownloadIcon />}
                            className="-mt-1"
                        />
                    </PDFDownloadLink>
                )}
            </div>
            {data ? (
                <div className="flex flex-col justify-around h-full">
                    <CustomTable
                        columns={columns}
                        data={data}
                        pageSize={5}
                        withPagination
                    />
                </div>
            ) : (
                <div className="w-full h-full flex justify-center items-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            )}
        </CustomPaper>
    )
}

export default PostgraduateStudies
